import { useCallback, useEffect, useRef, useState } from "react";
import TableMain from "../TableMain";
import TableSubheader from "../TableSubheader";
import Gear from "../icons/Gear/Gear";
import { PlayerService } from "../../services/Player.Services";
import { useParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { players } from "../../atoms/player";
import { match } from "../../atoms/match";
import PlayerSettings from "../PlayersSettings/PlayersSettings";
import { useTranslation } from "react-i18next";
import { time } from "../../atoms/time";
import {
  buildPlayerColumns,
  PlayerColumn,
  PlayerSort,
} from "../../utils/playerColumns";

const toggleSort = (currentSort: PlayerSort | null, columnId: string): PlayerSort => {
  if (currentSort?.columnId === columnId) {
    return {
      columnId,
      direction: currentSort.direction === "desc" ? "asc" : "desc",
    };
  }

  return {
    columnId,
    direction: "desc",
  };
};

export default function PlayersTactics() {
  let { id } = useParams();
  const [isShow, setIsShow] = useState(false);
  const { t } = useTranslation();
  const [playerData, setPlayersData] = useRecoilState(players);
  const matches = useRecoilValue(match);
  const times = useRecoilValue(time);
  const [playerColumns, setPlayerColumns] = useState<PlayerColumn[]>([]);
  const [currSortHome, setCurrSortHome] = useState<PlayerSort | null>(null);
  const [currSortAway, setCurrSortAway] = useState<PlayerSort | null>(null);
  const { away_team, home_team } = playerData;
  const homeColor = matches.home_team.color || "#C90F32";
  const awayColor = matches.away_team.color || "#00448B";
  const homeImage = `${matches.home_team.logo_base || ""}${matches.home_team.logo || ""}`;
  const awayImage = `${matches.away_team.logo_base || ""}${matches.away_team.logo || ""}`;
  const intervalRef = useRef<NodeJS.Timer | number | null>(null);
  const homeScrollRef = useRef<HTMLDivElement | null>(null);
  const awayScrollRef = useRef<HTMLDivElement | null>(null);
  const isSyncingScrollRef = useRef(false);
  const { analyse_status } = matches;

  const fetchData = useCallback(async () => {
    const response = await PlayerService.getPlayer(Number(id));
    const newData = await response.data;
    setPlayersData(newData);
  }, [id, setPlayersData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPlayerColumns((previousColumns) =>
      buildPlayerColumns(playerData, previousColumns)
    );
  }, [playerData]);

  useEffect(() => {
    const visibleColumnIds = new Set(
      playerColumns.filter((column) => column.isShow).map((column) => column.id)
    );

    setCurrSortHome((previousSort) => {
      if (!previousSort || visibleColumnIds.has(previousSort.columnId)) {
        return previousSort;
      }
      return null;
    });

    setCurrSortAway((previousSort) => {
      if (!previousSort || visibleColumnIds.has(previousSort.columnId)) {
        return previousSort;
      }
      return null;
    });
  }, [playerColumns]);

  useEffect(() => {
    if (analyse_status !== 2) {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current as NodeJS.Timer);
        intervalRef.current = null;
      }
      return;
    }

    const interval = setInterval(fetchData, times.time);
    intervalRef.current = interval;

    return () => {
      clearInterval(interval);
    };
  }, [analyse_status, fetchData, times.time]);

  const handleSettingsToggle = () => {
    setIsShow((previousState) => !previousState);
  };

  const handleSettingsClose = () => {
    setIsShow(false);
  };

  const handleColumnsConfirm = (nextColumns: PlayerColumn[]) => {
    setPlayerColumns(nextColumns);
  };

  const handleChangeSortHome = (columnId: string) => {
    setCurrSortHome((previousSort) => toggleSort(previousSort, columnId));
  };

  const handleChangeSortAway = (columnId: string) => {
    setCurrSortAway((previousSort) => toggleSort(previousSort, columnId));
  };

  const syncHorizontalScroll = useCallback(
    (
      sourceRef: { current: HTMLDivElement | null },
      targetRef: { current: HTMLDivElement | null }
    ) => {
      if (isSyncingScrollRef.current) {
        return;
      }

      const sourceElement = sourceRef.current;
      const targetElement = targetRef.current;

      if (!sourceElement || !targetElement) {
        return;
      }

      isSyncingScrollRef.current = true;
      targetElement.scrollLeft = sourceElement.scrollLeft;

      window.requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    },
    []
  );

  const handleHomeScroll = () => {
    syncHorizontalScroll(homeScrollRef, awayScrollRef);
  };

  const handleAwayScroll = () => {
    syncHorizontalScroll(awayScrollRef, homeScrollRef);
  };

  return (
    <div className="shadow-teams rounded-[10px] min-w-0">
      <div>
        <div className="bg-[#6886BD] dark:bg-[#B4EB54] py-[7px] rounded-t-[10px] text-white dark:text-black text-center underline text-[20px] relative">
          <p>{t("players").toLocaleUpperCase()}</p>
          <div
            onClick={handleSettingsToggle}
            className="absolute top-[10px] right-[10px] cursor-pointer"
          >
            <Gear color={"#77abff"} />
          </div>
        </div>
      </div>
      <div className="flex flex-col relative min-w-0">
        <div
          ref={homeScrollRef}
          onScroll={handleHomeScroll}
          className="overflow-x-auto overflow-y-hidden max-w-full"
        >
          <TableSubheader
            columns={playerColumns}
            sort={currSortHome}
            onSort={handleChangeSortHome}
          />
          <TableMain
            logo={homeImage}
            color={homeColor}
            columns={playerColumns}
            sort={currSortHome}
            players={home_team || { id: 0, name: "", players_stat: [] }}
          />
        </div>
        <div
          ref={awayScrollRef}
          onScroll={handleAwayScroll}
          className="overflow-x-auto overflow-y-hidden max-w-full"
        >
          <TableSubheader
            columns={playerColumns}
            sort={currSortAway}
            onSort={handleChangeSortAway}
          />
          <TableMain
            lastChild
            logo={awayImage}
            color={awayColor}
            columns={playerColumns}
            sort={currSortAway}
            players={away_team || { id: 0, name: "", players_stat: [] }}
          />
        </div>
        {isShow && (
          <PlayerSettings
            columns={playerColumns}
            onConfirm={handleColumnsConfirm}
            onClose={handleSettingsClose}
          />
        )}
      </div>
    </div>
  );
}
